import React, { useEffect, useState } from 'react';
import { useImageModal } from '../contexts/ImageModalContext';

const ImageModal = () => {
  const { modalState, closeModal, goToNext, goToPrev, setCurrentIndex } = useImageModal();
  const [localTransitioning, setLocalTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState('');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!modalState.isOpen || modalState.transitioning || localTransitioning) return;
      
      switch (e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowLeft':
          handlePrev();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        default:
          break;
      }
    };

    if (modalState.isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [modalState.isOpen, modalState.transitioning, localTransitioning, closeModal, goToNext, goToPrev]);

  const handleNext = () => {
    if (localTransitioning) return;
    setLocalTransitioning(true);
    setSlideDirection('slide-out-left');
    
    setTimeout(() => {
      goToNext();
      setSlideDirection('slide-in-right');
      setTimeout(() => {
        setLocalTransitioning(false);
        setSlideDirection('');
      }, 300);
    }, 150);
  };

  const handlePrev = () => {
    if (localTransitioning) return;
    setLocalTransitioning(true);
    setSlideDirection('slide-out-right');
    
    setTimeout(() => {
      goToPrev();
      setSlideDirection('slide-in-left');
      setTimeout(() => {
        setLocalTransitioning(false);
        setSlideDirection('');
      }, 300);
    }, 150);
  };

  const handleSwipe = (e) => {
    if (localTransitioning) return;
    
    const touchStartX = e.touches[0].clientX;
    const touchEndX = (e) => {
      const touchEnd = e.changedTouches[0].clientX;
      const diff = touchStartX - touchEnd;
      
      if (Math.abs(diff) > 50) { // Minimum swipe distance
        if (diff > 0) {
          handleNext();
        } else {
          handlePrev();
        }
      }
    };

    e.target.addEventListener('touchend', touchEndX, { once: true });
  };

  const handleThumbnailClick = (index) => {
    if (index !== modalState.currentIndex && !localTransitioning) {
      setLocalTransitioning(true);
      setSlideDirection(index > modalState.currentIndex ? 'slide-in-right' : 'slide-in-left');
      setTimeout(() => {
        setCurrentIndex(index);
        setLocalTransitioning(false);
        setSlideDirection('');
      }, 300);
    }
  };

  if (!modalState.isOpen || !modalState.images || modalState.images.length === 0) return null;

  const currentImage = modalState.images[modalState.currentIndex];
  const isTransitioning = modalState.transitioning || localTransitioning;

  return (
    <div className={`image-modal-overlay ${modalState.isOpen ? 'modal-open' : 'modal-closed'} ${modalState.transitioning ? 'modal-transitioning' : ''}`} onClick={closeModal}>
      <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={closeModal}>
          ✕
        </button>
        
        <button className="modal-nav-btn modal-prev-btn" onClick={handlePrev} disabled={isTransitioning}>
          ‹
        </button>
        
        <div className="modal-image-container" onTouchStart={handleSwipe}>
          <div className={`modal-image-wrapper ${slideDirection} ${modalState.direction}`}>
            <img 
              src={currentImage.src} 
              alt={currentImage.alt}
              className="modal-image"
            />
          </div>
          <div className="modal-image-info">
            <h3>{currentImage.alt}</h3>
            <p>{currentImage.description}</p>
          </div>
        </div>
        
        <button className="modal-nav-btn modal-next-btn" onClick={handleNext} disabled={isTransitioning}>
          ›
        </button>
        
        <div className="modal-counter">
          {modalState.currentIndex + 1} / {modalState.images.length}
        </div>
        
        <div className="modal-thumbnails">
          {modalState.images.map((image, index) => (
            <img
              key={index}
              src={image.src}
              alt={image.alt}
              className={`thumbnail ${index === modalState.currentIndex ? 'active' : ''}`}
              onClick={() => handleThumbnailClick(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
